import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AUTH_ACCESS_TOKEN, AUTH_CODE, AUTH_REFRESH_TOKEN } from '@lite-workspace/dto-auth';
import { AuthState, consumeLoginState, isSafeReturnUrl, writeAuthState } from './auth';
import { LoginCodeService } from './login-code.service';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div class="grid min-h-screen place-items-center text-c3">Входим…</div>`,
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly loginCodes = inject(LoginCodeService);
  private destroyed = false;

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;
    const code = params.get(AUTH_CODE);
    const accessToken = params.get(AUTH_ACCESS_TOKEN) ?? undefined;
    const refreshToken = params.get(AUTH_REFRESH_TOKEN) ?? undefined;
    const returnUrl = params.get('returnUrl');

    // A state in the URL must equal this tab's mint: one we never minted means the session was not
    // requested here (login CSRF / session fixation). A return with NO state is a cross-context
    // handoff — admin impersonation, magic-link tooling, identity login without ?callback — and is
    // accepted even while a mint is pending. Nothing clears the mint of a login the user abandoned
    // (clearAuth touches authState only), so gating the handoff on it would silently drop the tokens
    // of the login they just completed in the same tab; and refusing that half pair buys nothing,
    // since a forged token URL simply omits state and takes the very same lane. The mint is consumed
    // either way, so a replay finds nothing.
    // A host that has no cross-context entry at all opts out of the stateless lane through its
    // route data — then a return with no state is refused, the way that app's own callback did
    // before it moved here. The lane stays the default for the hosts that need it.
    const requireLoginState = this.route.snapshot.data?.['requireLoginState'] === true;
    const expectedState = consumeLoginState();
    const state = params.get('state');
    const trusted = state ? state === expectedState : !requireLoginState;

    // The code or tokens rode in as query params — scrub the browser history entry that carries
    // them so back-navigation, session restore or history sync can never replay the URL; replaceUrl
    // then keeps the callback itself out of history on a shared device. Scrubbed before the exchange
    // awaits, so the code leaves the address bar at once.
    window.history.replaceState(null, '', '/auth/callback');

    // The state check above gates the exchange too: an untrusted return never spends the code. The
    // code wins over tokens that ride next to it. While those tokens still ride in the query, a failed
    // exchange falls back to them — a Redis hiccup, a gateway mid-rollout or a 429 must not log the
    // user out; once the tokens leave the query the fallback has nothing to read.
    let issued: AuthState | null = null;
    if (trusted && code) {
      issued = await this.loginCodes.exchange(code);
      // RACE-SAFE: the login state was consumed before the await, so a replay finds nothing; a
      // callback torn down meanwhile (another navigation won) must neither write a session nor
      // navigate over the page the user is on now.
      if (this.destroyed) {
        return;
      }
    }

    if (!issued && trusted && accessToken) {
      issued = { accessToken, refreshToken };
    }

    if (issued) {
      writeAuthState(issued);
    }

    void this.router.navigateByUrl(trusted && isSafeReturnUrl(returnUrl) ? returnUrl : '/', { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }
}
